import React, { useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Box,
  VStack,
  Text,
  Input,
  InputGroup,
  InputLeftAddon,
  Textarea,
  Button,
  HStack,
  IconButton,
  AlertDialog,
} from "@chakra-ui/react";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  useUpdateActivityMutation,
  useDeleteActivityMutation,
} from "@store/api/activities";
import { useToastNotification } from "@hooks";
import { activityTitles } from "@constants/activities";
import { getActivityBgColor } from "@utils/theme/colors";

import ActivityHeader from "./ActivityHeader";
import TimingSelector from "./TimingSelector";

const NoteCreationModal = ({
  isOpen,
  onClose,
  activityType = "note",
  onSave,
  initialData = null, // For editing mode
  onInitiateRemix = null, // Function to initiate remix flow
}) => {
  const [updateActivity] = useUpdateActivityMutation();
  const [deleteActivity] = useDeleteActivityMutation();
  const { showSuccessToast, showErrorToast } = useToastNotification();
  const isEditMode = !!initialData;
  const [createdActivity, setCreatedActivity] = React.useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const showDeleteButton = false;
  const validationSchema = Yup.object({
    link: Yup.string()
      .test("is-valid-url", "Please enter a valid URL", function (value) {
        if (!value) return true; // Allow empty values for optional links
        try {
          // If URL doesn't start with http, prepend https:// for validation
          const urlToValidate = value.startsWith("http")
            ? value
            : `https://${value}`;
          new URL(urlToValidate);
          return true;
        } catch {
          return false;
        }
      })
      .when([], {
        is: () => activityType === "link",
        then: (schema) =>
          schema.required("Link is required for Link Share activities"),
        otherwise: (schema) => schema.optional(),
      }),
    message: Yup.string(),
    timing: Yup.string().oneOf(["begin", "middle", "end"]).required(),
  });

  // Extract link from options array or options object
  const getInitialLink = () => {
    let link = "";
    if (initialData?.options) {
      // Check if options is an array (backend format)
      if (
        Array.isArray(initialData.options) &&
        initialData.options[0]?.option
      ) {
        link = initialData.options[0].option;
      }
      // Check if options has a link property (old format)
      if (initialData.options.link) {
        link = initialData.options.link;
      }
    }

    // Remove https:// prefix if present since the InputLeftAddon will provide it
    if (link && link.startsWith("https://")) {
      return link.substring(8);
    }
    if (link && link.startsWith("http://")) {
      return link.substring(7);
    }
    return link;
  };

  // Extract message from content or options
  const getInitialMessage = () => {
    // First check content field (backend format)
    if (initialData?.content) {
      return initialData.content;
    }
    // Fallback to options.message (old format)
    if (initialData?.options?.message) {
      return initialData.options.message;
    }
    return "";
  };

  const formik = useFormik({
    initialValues: {
      title: initialData?.title || "",
      link: getInitialLink(),
      message: getInitialMessage(),
      timing: initialData?.settings?.timing || "begin",
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        if (isEditMode) {
          // Update existing activity - transform to backend format
          const formattedLink =
            values.link && values.link.trim()
              ? values.link.startsWith("http")
                ? values.link
                : `https://${values.link}`
              : "";

          const activityBody = {
            type: "share",
            title: values.title,
            content: values.message,
            link: formattedLink || null,
            settings: {
              persistent: true,
              blast: true,
              timing: values.timing,
            },
          };

          await updateActivity({
            activityId: initialData._id,
            body: activityBody,
          }).unwrap();

          const activityTitle = activityTitles[activityType] || "Activity";
          showSuccessToast(`${activityTitle} updated successfully`);

          // Call onSave callback for edit mode to notify parent component
          await onSave?.(activityBody);
          onClose();
        } else {
          // Create new activity - transform to expected format
          const formattedLink =
            values.link && values.link.trim()
              ? values.link.startsWith("http")
                ? values.link
                : `https://${values.link}`
              : "";

          const transformedValues = {
            title: values.title,
            options: {
              link: formattedLink,
              message: values.message,
            },
            settings: {
              timing: values.timing,
            },
          };
          const result = await onSave?.(transformedValues);
          if (result && !isEditMode) {
            setCreatedActivity(result);
            // Reset form after successful creation
            formik.resetForm();
          }
        }
      } catch (error) {
        const activityTitle = activityTitles[activityType] || "Activity";
        const action = isEditMode ? "updating" : "creating";
        showErrorToast(
          error.data?.message || `Error ${action} ${activityTitle}`,
        );
        console.error(`Error ${action} note activity:`, error);
      }
    },
  });

  const handleCancel = () => {
    onClose();
  };

  const handleBack = () => {
    onClose();
  };

  const handleRemixClick = () => {
    if (onInitiateRemix && initialData) {
      onInitiateRemix(initialData);
    }
  };

  const handleDeleteActivity = async () => {
    try {
      await deleteActivity(initialData._id).unwrap();
      const activityTitle = activityTitles[activityType] || "Activity";
      showSuccessToast(`${activityTitle} deleted successfully`);
      onClose();
    } catch (error) {
      const activityTitle = activityTitles[activityType] || "Activity";
      showErrorToast(error.data?.message || `Error deleting ${activityTitle}`);
      console.error(`Error deleting activity:`, error);
    }
  };

  const handleDeleteClick = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setIsDeleteDialogOpen(false);
  };

  // Determine the display title based on mode and state
  const getDisplayTitle = () => {
    if (isEditMode && initialData?.displayName) {
      // Edit mode: use existing display name
      return initialData.displayName;
    } else if (createdActivity?.displayName) {
      // Creation complete: use generated display name
      return createdActivity.displayName;
    } else if (formik.isSubmitting) {
      // Currently creating: show loading state
      return "Creating Activity...";
    } else {
      // Initial creation state: show generic title
      return activityType === "link" || activityType === "share"
        ? "Link Share"
        : "Trainer's Tip";
    }
  };

  // Determine the emoji based on mode and state
  const getEmoji = () => {
    if (isEditMode && initialData?.emoji) {
      // Edit mode: use existing emoji
      return initialData.emoji;
    } else if (createdActivity?.emoji) {
      // Creation complete: use generated emoji
      return createdActivity.emoji;
    } else {
      // Default emoji based on activity type
      return activityType === "link" || activityType === "share" ? "📚" : "💡";
    }
  };

  const title = getDisplayTitle();
  const icon = getEmoji();

  // Get the activity color based on type and current data
  const activityColor = getActivityBgColor(activityType, initialData);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent
        bg="white"
        borderRadius="14px"
        // maxW={{ base: "95%", md: HOST_CONTAINER_WIDTH }}
        maxW="424px"
        mx={4}
        maxH="90vh"
        overflowY="auto"
      >
        <ModalBody p={0}>
          <VStack spacing={6} align="stretch" p="14px 14px 16px">
            {/* Header */}
            <ActivityHeader
              icon={icon}
              title={title}
              color={activityColor}
              onCancel={handleCancel}
              onSave={formik.handleSubmit}
              isLoading={formik.isSubmitting}
              isDisabled={!formik.isValid || formik.isSubmitting}
            />

            {/* Content */}
            <Box border="1px solid #F6F5F7" borderRadius="8px" p={3}>
              <VStack spacing={5} align="stretch">
                {/* Header with Back Button */}
                <HStack justify="space-between" align="center">
                  <Text
                    fontSize="14px"
                    fontWeight="84"
                    lineHeight="1.43"
                    letterSpacing="-0.02em"
                    color="#0C001C"
                    fontFamily="Geist, sans-serif"
                    flex={1}
                  >
                    Link Details
                  </Text>
                  <IconButton
                    icon={<ArrowBackIcon />}
                    size="sm"
                    variant="ghost"
                    color="#0C001C"
                    w="24px"
                    h="24px"
                    minW="24px"
                    onClick={handleBack}
                    aria-label="Go back"
                  />
                </HStack>

                {/* Form Fields */}
                <VStack spacing={5} align="stretch">
                  {/* Title */}
                  <VStack spacing="6px" align="stretch">
                    <Text
                      fontSize="11px"
                      fontWeight="84"
                      lineHeight="1.45"
                      letterSpacing="-0.01em"
                      color="#0C001C"
                      fontFamily="Geist, sans-serif"
                    >
                      Text to Display
                    </Text>
                    <Input
                      name="title"
                      value={formik.values.title}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      placeholder="Activity title"
                      bg="#F6F5F7"
                      border="none"
                      borderRadius="4px"
                      p={3}
                      fontSize="11px"
                      fontWeight="84"
                      lineHeight="1.45"
                      letterSpacing="-0.01em"
                      color="#726682"
                      fontFamily="Geist, sans-serif"
                      _focus={{
                        bg: "white",
                        borderColor: "#4C47CC",
                        boxShadow: "0 0 0 1px #4C47CC",
                      }}
                      data-testid="send-message-input"
                    />
                  </VStack>

                  {/* Link - Show for link activities or share activities with links */}
                  {(activityType === "link" ||
                    (activityType === "share" && getInitialLink())) && (
                    <VStack spacing="6px" align="stretch">
                      <Text
                        fontSize="11px"
                        fontWeight="84"
                        lineHeight="1.45"
                        letterSpacing="-0.01em"
                        color="#0C001C"
                        fontFamily="Geist, sans-serif"
                      >
                        Link
                      </Text>
                      <Box bg="#F6F5F7" borderRadius="4px" overflow="hidden">
                        <InputGroup>
                          <InputLeftAddon
                            children="https://"
                            bg="#E2E8F0"
                            border="none"
                            fontSize="11px"
                            color="#726682"
                            px={3}
                            py={0}
                            height="auto"
                            minH="44px"
                            fontFamily="Geist, sans-serif"
                          />
                          <Input
                            name="link"
                            value={formik.values.link}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            placeholder="linkgoeshere.com"
                            bg="transparent"
                            border="none"
                            p={3}
                            fontSize="11px"
                            fontWeight="84"
                            lineHeight="1.45"
                            letterSpacing="-0.01em"
                            color="#726682"
                            fontFamily="Geist, sans-serif"
                            _focus={{
                              bg: "white",
                              borderColor: "#4C47CC",
                              boxShadow: "0 0 0 1px #4C47CC",
                            }}
                            data-testid="send-link-input-0"
                          />
                        </InputGroup>
                      </Box>
                      {formik.touched.link && formik.errors.link && (
                        <Text fontSize="sm" color="red.500">
                          {formik.errors.link}
                        </Text>
                      )}
                    </VStack>
                  )}

                  {/* Note/Message */}
                  <VStack spacing="6px" align="stretch">
                    <Text
                      fontSize="11px"
                      fontWeight="84"
                      lineHeight="1.45"
                      letterSpacing="-0.01em"
                      color="#0C001C"
                      fontFamily="Geist, sans-serif"
                    >
                      Note/Message:
                    </Text>
                    <Textarea
                      name="message"
                      value={formik.values.message}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      placeholder="E.g Read this brief before we discuss Q2 updates"
                      bg="#F6F5F7"
                      border="none"
                      borderRadius="4px"
                      p={3}
                      fontSize="11px"
                      fontWeight="84"
                      lineHeight="1.45"
                      letterSpacing="-0.01em"
                      color="#726682"
                      fontFamily="Geist, sans-serif"
                      resize="vertical"
                      minH="60px"
                      _focus={{
                        bg: "white",
                        borderColor: "#4C47CC",
                        boxShadow: "0 0 0 1px #4C47CC",
                      }}
                      data-testid="send-message-textarea"
                    />
                  </VStack>

                  {/* Timing */}
                  {/* TODO: Implement TimingSelector */}
                  {/* <VStack spacing="6px" align="stretch">
                    <Text
                      fontSize="11px"
                      fontWeight="84"
                      lineHeight="1.45"
                      letterSpacing="-0.01em"
                      color="#0C001C"
                      fontFamily="Geist, sans-serif"
                    >
                      When should this link/note show in the meeting?
                    </Text>
                    <TimingSelector
                      value={formik.values.timing}
                      onChange={(value) =>
                        formik.setFieldValue("timing", value)
                      }
                    />
                  </VStack> */}
                </VStack>
              </VStack>
            </Box>

            {/* Footer */}
            <VStack spacing={3} align="stretch" pt={2}>
              {/* Remix Button - only show in edit mode */}
              {isEditMode && (
                <Button
                  bg="#4C47CC"
                  color="white"
                  fontSize="13px"
                  fontWeight="700"
                  lineHeight="1.23"
                  fontFamily="Geist, sans-serif"
                  borderRadius="8px"
                  py={4}
                  px={14}
                  h="auto"
                  _hover={{ bg: "#3D39A3" }}
                  _active={{ bg: "#3D39A3" }}
                  onClick={handleRemixClick}
                >
                  ✨ Tired of Typing? Remix it!
                </Button>
              )}

              {/* Footer Links */}
              <HStack justify="space-between" spacing={4}>
                {isEditMode && showDeleteButton && (
                  <Button
                    variant="outline"
                    color="#D92D20"
                    borderColor="#FFFFFF"
                    fontSize="14px"
                    fontWeight="84"
                    lineHeight="1.43"
                    letterSpacing="-0.02em"
                    fontFamily="Geist, sans-serif"
                    textAlign="center"
                    h="24px"
                    px={2}
                    flex={1}
                    _hover={{ bg: "red.50" }}
                    onClick={handleDeleteClick}
                    isLoading={deleteActivity.isLoading}
                  >
                    Delete Activity
                  </Button>
                )}
              </HStack>
            </VStack>
          </VStack>
        </ModalBody>
      </ModalContent>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        title={`Delete ${activityTitles[activityType] || "Activity"}`}
        isOpen={isDeleteDialogOpen}
        onClose={handleDeleteCancel}
        primaryButton={
          <Button
            size="sm"
            colorScheme="red"
            onClick={handleDeleteActivity}
            isLoading={deleteActivity.isLoading}
            ml={3}
          >
            Delete
          </Button>
        }
        secondaryButton={
          <Button
            size="sm"
            onClick={handleDeleteCancel}
            bg="white"
            disabled={deleteActivity.isLoading}
          >
            Cancel
          </Button>
        }
      >
        {`Are you sure you want to delete this ${
          activityTitles[activityType] || "Activity"
        }?`}
      </AlertDialog>
    </Modal>
  );
};

export default NoteCreationModal;
